import axios from 'axios';
import Cookies from 'js-cookie';

export const getToken = () => {
  return Cookies.get('access_token');
};

export const setToken = access_token => {
  return Cookies.set('access_token', access_token);
};

export const removeToken = () => {
  Cookies.remove('access_token');
};

// Token refresh throttling
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 2 * 60 * 1000; // 2 minutes in milliseconds
let refreshPromise = null;

// Axios instance configuration
export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  async config => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.error('No token found');
    }
    return config;
  },
  error => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Only attempt refresh on 401/403 errors, when not already retrying, and when endpoint is not the refresh endpoint itself
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/jwt/refresh')
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await getRefreshToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Throttled token refresh function
async function getRefreshToken() {
  const now = Date.now();

  // If another refresh is in progress, wait for it instead of starting a new one
  if (refreshPromise) {
    return refreshPromise;
  }

  // If we've refreshed recently, use the existing token
  if (now - lastRefreshTime < REFRESH_COOLDOWN) {
    const currentToken = getToken();
    if (currentToken) {
      console.log('Using existing token, refresh on cooldown');
      return currentToken;
    }
  }

  // Start a new refresh
  try {
    refreshPromise = refreshToken();
    const newToken = await refreshPromise;
    return newToken;
  } finally {
    refreshPromise = null;
  }
}

// Actual token refresh API call
async function refreshToken() {
  try {
    console.log('Refreshing token');
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_API_URL}/auth/jwt/refresh`,
      {},
      { withCredentials: true }
    );

    const newToken = response?.data?.access_token;
    if (newToken) {
      setToken(newToken);
      lastRefreshTime = Date.now();
      return newToken;
    } else {
      throw new Error('No token received from refresh endpoint');
    }
  } catch (error) {
    console.error('Error refreshing token', error);
    throw error;
  }
}
